import { Injectable, Injector } from '@angular/core';
import { AbcCore, CompareHelper, ConvertHelper, GUID } from '@barthauer/abc/core';
import { LoginInfo, ProfileService, UserService } from '@barthauer/abc/core/authentication';
import {
  ICoreDataEntity,
  ObjectKey,
  PersistMode,
  PersistObjectModel,
  PersistObjectsModel,
  Query,
  QueryColumn,
  QueryColumnSortOrder,
  QueryObjectsModel,
  TargetColumnValue,
  TargetObjectData,
} from '@barthauer/abc/core/data';
import { ParameterValue } from '@barthauer/abc/queries/parameters/core';
import { ObjectSelection, ObjectSelectionItem, ObjectsSet, ObjectsSetHelper } from './models/objects-set';
import { ObjectsSetsSettings } from './objects-sets-column-info';

@Injectable()
export class ObjectsSetsService {
  private objectSelectionsItemListIds: Map<string, ObjectSelectionItem[]> = new Map();
  private objectSelectionsItemList: { [key: string]: ObjectSelectionItem[] } = {};

  public currentUserInfo: LoginInfo;
  public readonly persistenceCategory: string = 'Selections';
  public updatedDataEntity: (entity: ICoreDataEntity) => Promise<void>;
  public objectSelections: Array<ObjectsSet> = [];
  public containerId: string;
  public selectedEntities: { idExpression: string; objectTypeExpression: string; entities: ObjectSelectionItem[] } = {
    entities: [],
    idExpression: '',
    objectTypeExpression: '',
  };

  constructor(public core: AbcCore, private injector: Injector) {}

  public async isReadOnly(): Promise<boolean> {
    const profileInfo = await this.injector.get(ProfileService)?.getCurrentProfile(true).toPromise();
    return ConvertHelper.toBoolean(profileInfo?.settings?.Permissions?.isReadOnly);
  }

  public async getObjectsSelections(): Promise<ObjectsSet[]> {
    if (!this.currentUserInfo) {
      this.currentUserInfo = await this.injector.get(UserService).getCurrentLoginInfo().toPromise();
    }
    this.containerId = await ObjectsSetsSettings.getCurrentContainerId();
    if (this.objectSelections.length === 0) {
      const objectSelections = await this.getSelectionsCore();
      await this.pushToObjectSelections(objectSelections);
    }
    return this.objectSelections;
  }

  public async getObjectsSelectionById(id: string): Promise<ObjectsSet | undefined> {
    const objectSelection = ConvertHelper.arrayBufferToObject<ObjectSelection>(
      await this.core.data.executeReadObjectsQuery(this.selectionQueryObjectModel(`Id ='${id}'`)).toPromise()
    );

    if (objectSelection) {
      const objectsSet = await this.createObjectsSetViewModel(objectSelection);
      const oldObjectSetIndex = this.objectSelections.findIndex((os) => os.id === id);
      if (oldObjectSetIndex > -1) {
        this.objectSelections[oldObjectSetIndex] = objectsSet;
      }
      return objectsSet;
    }
    return undefined;
  }

  private async pushToObjectSelections(objectSelections: ObjectSelection[]): Promise<ObjectsSet[]> {
    const objectsSets: ObjectsSet[] = [];
    for (let index = 0; index < objectSelections.length; index++) {
      const objectSelection = objectSelections[index];
      const objectsSet = await this.createObjectsSetViewModel(objectSelection);
      this.objectSelections.push(objectsSet);
      objectsSets.push(objectsSet);
    }
    return objectsSets;
  }

  private async createObjectsSetViewModel(objectSelection: ObjectSelection): Promise<ObjectsSet> {
    const objectsSet = new ObjectsSet(
      {
        id: objectSelection.Id,
        objectType: 'Selection',
        persistenceCategory: this.persistenceCategory,
      },
      objectSelection.Designation
    );
    objectsSet.persist = () => this.core.persist;
    objectsSet.loginInfo = this.currentUserInfo;
    objectsSet.id = objectSelection.Id;
    objectsSet.orderUnitType =
      (await this.core.objectSpace.getObjectType(objectSelection.ReferencedObjectType).toPromise())?.name ||
      ObjectsSetsSettings.orderUnits?.find((o) => o.name === 'Other')?.caption ||
      '';
    objectsSet.entities = async () => {
      this.objectSelectionsItemListIds = new Map();
      if (CompareHelper.isEmptyObject(this.objectSelectionsItemList[objectSelection.Id])) {
        const selectionItemList = await this.getSelectionsItemList(objectSelection.Id);
        this.objectSelectionsItemList[objectSelection.Id] = selectionItemList;
      }
      return this.objectSelectionsItemList[objectSelection.Id];
    };
    objectsSet.checkVisibility(objectSelection.Designation);
    objectsSet.lastSavedDateTime = ObjectsSetHelper.setLastSavedDateTime(
      objectSelection.SysDateUpdate,
      objectSelection.SysTimeUpdate
    );
    objectsSet.entitiesCount = objectSelection.ObjectSelectionItemCount;
    return objectsSet;
  }

  private async getSelectionsCore(): Promise<ObjectSelection[]> {
    return ConvertHelper.arrayBufferToObjectArray<ObjectSelection>(
      await this.core.data
        .executeReadObjectsQuery(this.selectionQueryObjectModel(`Type = 10 And ContainerId='${this.containerId}'`))
        .toPromise()
    );
  }

  private async getSelectionsItemList(objectSelectionId: string): Promise<ObjectSelectionItem[]> {
    const flatObjectSelectionItems: ObjectSelectionItem[] = [];
    const query = this.core.data.createSimpleQuery('', 'SelectionItem', "ParentId = '" + objectSelectionId + "'", [
      {
        Name: 'IdObjectSelectionItem',
        OPath: 'Id',
      },
      {
        Name: 'ObjectType',
        OPath: 'ObjectTypeRefObject',
      },
      {
        Name: 'Id',
        OPath: 'IdRefObject',
      },
    ]);
    const osil: ObjectSelectionItem[] =
      ConvertHelper.arrayBufferToObjectArray<ObjectSelectionItem>(
        await this.core.data.executeReadObjectsQuery(query).toPromise()
      ) || [];

    await this.groupItemsByObjectType(osil);
    const itemsDisplayText = await this.queryDisplayTexts();
    for (const key in itemsDisplayText) {
      if (Object.prototype.hasOwnProperty.call(itemsDisplayText, key)) {
        const items = this.objectSelectionsItemListIds.get(key);
        const otvm = await this.core.objectSpace.getObjectType(key).toPromise();
        const itemsWithDT = itemsDisplayText[key];
        for (let index = 0; index < itemsWithDT.length; index++) {
          const item = itemsWithDT[index];

          const it = items?.find((i) => item.Id === i.Id);
          if (it) {
            it.Id_DisplayText = item.Id_DisplayText;
            it.ObjectType_DisplayText = otvm.uniqueDisplayText;
            flatObjectSelectionItems.push(it);
          }
        }
      }
    }
    return flatObjectSelectionItems;
  }

  private async groupItemsByObjectType(objectSelectionItemList: ObjectSelectionItem[]): Promise<void> {
    this.objectSelectionsItemListIds = new Map();
    for (let index = 0; index < objectSelectionItemList.length; index++) {
      const item = objectSelectionItemList[index];
      const otvm = await this.core.objectSpace.getObjectType(item.ObjectType).toPromise();
      if (this.objectSelectionsItemListIds.has(otvm?.name)) {
        this.objectSelectionsItemListIds.get(otvm.name)?.push(item);
      } else {
        this.objectSelectionsItemListIds.set(otvm.name, [item]);
      }
    }
  }

  public async queryDisplayTexts(): Promise<{ [key: string]: { Id_DisplayText: string; Id: string }[] } | undefined> {
    const qom: QueryObjectsModel = new QueryObjectsModel();
    qom.Parameters = [];
    qom.ObjectQueries = [];
    for (const iterator of this.objectSelectionsItemListIds) {
      const ot = iterator[0];
      const osil = iterator[1];
      const query: Query = new Query();
      query.ObjectType = ot;
      query.Name = ot;
      query.OPath = `Id In ($IdList_${ot})`;
      query.Columns = [
        {
          Name: 'Id_DisplayText',
          OPath: 'ref:Id',
        },
        {
          Name: 'Id',
          OPath: 'Id',
        },
      ];
      let ids: string[] = [];
      for (let index = 0; index < osil.length; index++) {
        const item = osil[index];
        ids.push(item.Id);
      }
      qom.Parameters.push(new ParameterValue(`IdList_${ot}`, ids));
      qom.ObjectQueries.push(query);
    }

    return ConvertHelper.arrayBufferToObject<{ [key: string]: { Id_DisplayText: string; Id: string }[] }>(
      await this.core.data.executeReadObjectsQuery(qom).toPromise()
    );
  }

  public async saveObjectsSet(
    designation: string,
    entities: ICoreDataEntity[],
    referencedId?: string,
    referencedObjectType?: number
  ): Promise<ObjectsSet | unknown> {
    try {
      const pomSelectionItems: PersistObjectsModel = new PersistObjectsModel();
      const targetObjectData: TargetObjectData = new TargetObjectData();
      targetObjectData.Mode = PersistMode.Insert;
      targetObjectData.ObjectKey = new ObjectKey();
      targetObjectData.ObjectKey.ObjectType = 'Selection';
      const selectionId = GUID.newGUID();
      const targetColumns: TargetColumnValue[] = [
        {
          Name: 'Designation',
          Value: designation,
        },
        {
          Name: 'ObjectTypeRef',
          Value: 0,
        },
        {
          Name: 'Type',
          Value: 10,
        },
        {
          Name: 'Id',
          Value: selectionId,
        },
        {
          Name: 'ContainerId',
          Value: this.containerId,
        },
      ];

      if (referencedId) {
        targetColumns.push({ Name: 'ReferencedId', Value: referencedId });
      }
      if (referencedObjectType) {
        targetColumns.push({ Name: 'ReferencedObjectType', Value: referencedObjectType });
      }

      targetObjectData.TargetColumns = targetColumns;
      pomSelectionItems.Objects = [[targetObjectData]];
      for (let index = 0; index < entities.length; index++) {
        const entity = entities[index];
        const targetObjectData: TargetObjectData = new TargetObjectData();
        targetObjectData.Mode = PersistMode.Insert;
        targetObjectData.ObjectKey = new ObjectKey();
        targetObjectData.ObjectKey.ObjectType = 'SelectionItem';
        const targetColumns: TargetColumnValue[] = [
          { Name: 'ParentId', Value: selectionId },
          {
            Name: 'IdRefObject',
            Value: entity.Id,
          },
          {
            Name: 'ObjectTypeRefObject',
            Value: entity.ObjectType,
          },
        ];
        targetObjectData.TargetColumns = targetColumns;
        pomSelectionItems.Objects.push([targetObjectData]);
      }
      const persistedObjects = ConvertHelper.arrayBufferToObject<ICoreDataEntity[]>(
        await this.core.persist.executePersistObjectsQuery(pomSelectionItems).toPromise()
      );

      if (persistedObjects) {
        const objectSelection = ConvertHelper.arrayBufferToObject<ObjectSelection>(
          await this.core.data
            .executeReadObjectsQuery(this.selectionQueryObjectModel(`Id = '${persistedObjects[0].Id}'`))
            .toPromise()
        );

        if (objectSelection) {
          return (await this.pushToObjectSelections([objectSelection]))[0];
        }
      } else {
        return [];
      }
      return;
    } catch (err) {
      this.core.error.publish(err);
      return err;
    }
  }

  public async updateObjectsSet(
    selectionId: string,
    entities: ICoreDataEntity[],
    deleteExistingItems?: boolean,
    existingItems?: ObjectSelectionItem[]
  ): Promise<ICoreDataEntity[]> {
    const pomSelectionItems: PersistObjectsModel = new PersistObjectsModel();
    pomSelectionItems.Objects = [];

    if (deleteExistingItems) {
      if (existingItems?.length) {
        const objectsToDelete: TargetObjectData[] = [];
        for (let index = 0; index < existingItems.length; index++) {
          const existingItem = existingItems[index];
          const targetObjectDataToDelete: TargetObjectData = new TargetObjectData();
          targetObjectDataToDelete.Mode = PersistMode.Delete;
          targetObjectDataToDelete.ObjectKey = new ObjectKey();
          targetObjectDataToDelete.ObjectKey.ObjectType = 'SelectionItem';
          targetObjectDataToDelete.ObjectKey.Id = existingItem.IdObjectSelectionItem;
          objectsToDelete.push(targetObjectDataToDelete);
        }
        pomSelectionItems.Objects.push(objectsToDelete);
      }
    }
    const objectsToInsert: TargetObjectData[] = [];
    for (let index = 0; index < entities.length; index++) {
      const entity = entities[index];
      const targetObjectData: TargetObjectData = new TargetObjectData();
      targetObjectData.Mode = PersistMode.Insert;
      targetObjectData.ObjectKey = new ObjectKey();
      targetObjectData.ObjectKey.ObjectType = 'SelectionItem';
      const targetColumns: TargetColumnValue[] = [
        { Name: 'ParentId', Value: selectionId },
        {
          Name: 'IdRefObject',
          Value: entity.Id,
        },
        {
          Name: 'ObjectTypeRefObject',
          Value: entity.ObjectType,
        },
      ];
      targetObjectData.TargetColumns = targetColumns;

      objectsToInsert.push(targetObjectData);
    }
    pomSelectionItems.Objects.push(objectsToInsert);
    const persistedObjects = ConvertHelper.arrayBufferToObjectArray<ICoreDataEntity>(
      await this.core.persist.executePersistObjectsQuery(pomSelectionItems).toPromise()
    );
    // ReQuery new Selection items and store them.
    const selectionItemList = await this.getSelectionsItemList(selectionId);
    this.objectSelectionsItemList[selectionId] = selectionItemList;
    return persistedObjects;
  }

  public deleteObjectsSet(id: string): Promise<void> {
    return new Promise(async (resolve, reject) => {
      try {
        const persistObject: PersistObjectModel = new PersistObjectModel();
        const targetObjectData: TargetObjectData = new TargetObjectData();
        targetObjectData.Mode = PersistMode.Delete;
        targetObjectData.ObjectKey = new ObjectKey();
        targetObjectData.ObjectKey.ObjectType = 'Selection';
        targetObjectData.ObjectKey.Id = id;
        persistObject.Object = targetObjectData;

        await this.core.persist.executePersistObjectQuery(persistObject).toPromise();
        resolve();
      } catch (err: any) {
        reject(err.Message || err.error.Message);
      }
    });
  }

  private selectionQueryObjectModel(opath: string): QueryObjectsModel {
    const queryObjectModel: QueryObjectsModel = new QueryObjectsModel();
    const selectionsQueryObjectsModel: Query = new Query();
    selectionsQueryObjectsModel.ObjectType = 'Selection';
    selectionsQueryObjectsModel.OPath = opath;

    const selectionsQueryColumns: QueryColumn[] = [];
    selectionsQueryColumns.push(this.core.data.createQueryColumn('Id'));
    selectionsQueryColumns.push(this.core.data.createQueryColumn('ObjectType'));
    selectionsQueryColumns.push(
      this.core.data.createQueryColumn('Designation', 'Designation', QueryColumnSortOrder.Ascending)
    );
    selectionsQueryColumns.push(this.core.data.createQueryColumn('ContainerId'));
    selectionsQueryColumns.push(this.core.data.createQueryColumn('ObjectTypeRef'));
    selectionsQueryColumns.push(this.core.data.createQueryColumn('ReferencedId'));
    selectionsQueryColumns.push(this.core.data.createQueryColumn('ReferencedObjectType'));
    selectionsQueryColumns.push(this.core.data.createQueryColumn('SysDateUpdate'));
    selectionsQueryColumns.push(this.core.data.createQueryColumn('SysTimeUpdate'));
    selectionsQueryColumns.push(this.core.data.createQueryColumn('Type'));
    selectionsQueryColumns.push(this.core.data.createQueryColumn('UpdateDate'));
    selectionsQueryColumns.push(this.core.data.createQueryColumn('IdRefPersonCreator'));
    selectionsQueryColumns.push(
      this.core.data.createQueryColumn('Count(SelectionItemList[ParentId = ^.Id])', 'ObjectSelectionItemCount')
    );

    selectionsQueryObjectsModel.Columns = selectionsQueryColumns;

    queryObjectModel.ObjectQueries = [selectionsQueryObjectsModel];
    return queryObjectModel;
  }
}
